// The engine behind Dropdown and Select: a button (role=combobox) that
// opens a listbox rendered in a portal on document.body, so no overflow or
// transformed ancestor can clip it, positioned under the trigger (above it
// when there is no room) and kept on screen while the page scrolls.
//
// Keyboard: Arrow Up/Down open and move, Home/End jump, a letter jumps to
// the next option starting with it, Enter/Space pick, Escape closes
// without picking (and does not close a dialog behind it, wherever focus
// is), Tab closes. Focus stays on the trigger; the highlighted option is
// announced through aria-activedescendant and, when reached from the
// keyboard, outlined in the focus colour.
//
// Naming: `ariaLabel`, or `labelledBy` (the id of a visible label), or a
// <label htmlFor={id}> pointing at the trigger via `id`.

import { Fragment, useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";

import { OVERLAY_SHADOW, Z, cx } from "./controlStyles";
import { useAnchoredPosition, useFloatingLayer, useOutsidePress } from "./overlayHooks";

export interface ListboxOption<T extends string> {
  value: T;
  label: string;
  /** Second line under the label. */
  hint?: ReactNode;
  disabled?: boolean;
  /**
   * A heading shown above the first option of a run with this group, after
   * a divider ("Subagents" under the team). Options of one group must be
   * adjacent; ungrouped options come first.
   */
  group?: string;
}

interface ListboxFieldProps<T extends string> {
  value: T;
  options: ListboxOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  /** id of a visible element that names the field (aria-labelledby). */
  labelledBy?: string;
  /** id of the trigger button, for a <label htmlFor>. */
  id?: string;
  /** A field error: aria-invalid on the trigger (a form's "focus the first invalid field" finds it). */
  invalid?: boolean;
  /** id(s) of the help or error line (aria-describedby on the trigger). */
  describedBy?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Size, surface and text of the trigger button. */
  triggerClassName: string;
  className?: string;
  style?: CSSProperties;
  /** Minimum width of the open list (it is never narrower than the trigger). */
  listMinWidth?: number;
  align?: "start" | "end";
  /**
   * true: the list is exactly as wide as the trigger, start edges aligned,
   * options wrap. "auto" (default): the same for a trigger at least 240px
   * wide (a form field); a compact trigger keeps a content-sized list.
   * false: always content-sized.
   */
  matchTriggerWidth?: boolean | "auto";
  /**
   * What the trigger shows instead of the chosen option and its chevron: a
   * plain "Change" beside a sentence that says the choice in words.
   */
  trigger?: ReactNode;
  /** The open list's width in px (never wider than the screen); its options wrap. */
  listWidth?: number;
}

export function ListboxField<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  labelledBy,
  id,
  invalid,
  describedBy,
  placeholder = "Select",
  disabled,
  triggerClassName,
  className,
  style,
  listMinWidth = 0,
  align = "start",
  matchTriggerWidth = "auto",
  trigger: triggerContent,
  listWidth,
}: ListboxFieldProps<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  // true once the highlight has moved by keyboard (it gets the focus ring).
  const [keyboard, setKeyboard] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLUListElement>(null);
  const listId = useId();
  const selectedIndex = options.findIndex((o) => o.value === value);
  const current = selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const pos = useAnchoredPosition(trigger, panel, open, {
    align,
    minWidth: listWidth ?? listMinWidth,
    fit: listWidth ? false : matchTriggerWidth,
    matchWidth: !listWidth,
  });

  useOutsidePress([trigger, panel], open, () => setOpen(false));
  // A press on an option is inside a popover the field sits in, not outside it.
  useFloatingLayer(panel, open);

  // Escape closes the list even when focus is not on the trigger (Safari
  // does not focus a button on click) — captured first, so a dialog or
  // sheet behind the list stays open.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    }
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (!open || active < 0) return;
    panel.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  function step(from: number, dir: 1 | -1) {
    for (let n = 1; n <= options.length; n++) {
      const i = (from + dir * n + options.length * 2) % options.length;
      if (!options[i].disabled) return i;
    }
    return from;
  }

  function openList(byKeyboard = false) {
    if (disabled) return;
    setActive(selectedIndex >= 0 ? selectedIndex : step(-1, 1));
    setKeyboard(byKeyboard);
    setOpen(true);
    trigger.current?.focus({ preventScroll: true });
  }

  function pick(i: number) {
    const o = options[i];
    if (!o || o.disabled) return;
    if (o.value !== value) onChange(o.value);
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (disabled) return;
    const key = e.key;
    if (!open) {
      if (key === "ArrowDown" || key === "ArrowUp" || key === "Enter" || key === " ") {
        e.preventDefault();
        openList(true);
      }
      return;
    }
    if (key !== "Tab" && key !== "Enter" && key !== " ") setKeyboard(true);
    if (key === "Tab") {
      setOpen(false);
    } else if (key === "ArrowDown" || key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => step(i < 0 ? (key === "ArrowDown" ? -1 : 0) : i, key === "ArrowDown" ? 1 : -1));
    } else if (key === "Home" || key === "End") {
      e.preventDefault();
      setActive(key === "Home" ? step(-1, 1) : step(0, -1));
    } else if (key === "Enter" || key === " ") {
      e.preventDefault();
      pick(active);
    } else if (key.length === 1 && /\S/.test(key)) {
      const k = key.toLowerCase();
      for (let n = 1; n <= options.length; n++) {
        const i = (Math.max(active, 0) + n) % options.length;
        if (!options[i].disabled && options[i].label.toLowerCase().startsWith(k)) {
          setActive(i);
          break;
        }
      }
    }
  }

  const optionId = (i: number) => `${listId}-o${i}`;

  return (
    <div className={cx("relative inline-block", className)} style={style}>
      <button
        ref={trigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        aria-label={labelledBy ? undefined : ariaLabel}
        aria-labelledby={labelledBy}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        id={id}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className={cx(
          "flex w-full items-center justify-between gap-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45",
          triggerClassName,
        )}
      >
        {triggerContent ?? (
          <>
            <span className={cx("min-w-0 truncate", !current && "text-fg-subtle")}>
              {current?.label ?? placeholder}
            </span>
            <ChevronDown
              aria-hidden
              className={cx("h-4 w-4 shrink-0 text-fg-subtle transition-transform", open && "rotate-180")}
            />
          </>
        )}
      </button>
      {open &&
        createPortal(
          <ul
            ref={panel}
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            tabIndex={-1}
            className={cx("fixed overflow-y-auto rounded-control border border-line bg-overlay p-1 outline-none", OVERLAY_SHADOW)}
            style={{
              zIndex: Z.popover,
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              minWidth: pos?.minWidth,
              width: listWidth ?? pos?.width,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: pos ? Math.min(pos.maxHeight, 320) : 320,
              visibility: pos ? "visible" : "hidden",
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {options.map((o, i) => {
              const selected = o.value === value;
              const heading = o.group && o.group !== options[i - 1]?.group ? o.group : null;
              return (
                <Fragment key={o.value}>
                  {heading && (
                    <li
                      role="presentation"
                      className={cx("px-2 pb-1 pt-2 text-xs font-medium text-fg-subtle", i > 0 && "mt-1 border-t border-line-subtle")}
                    >
                      {heading}
                    </li>
                  )}
                  <li
                    id={optionId(i)}
                    data-index={i}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={o.disabled || undefined}
                    onMouseEnter={() => {
                      if (o.disabled) return;
                      setActive(i);
                      setKeyboard(false);
                    }}
                    onClick={() => pick(i)}
                    className={cx(
                      "flex min-h-[32px] cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5 [@media(pointer:coarse)]:min-h-[44px]",
                      i === active && "bg-[color:var(--c-glass-button-hover)]",
                      i === active && keyboard && "shadow-[inset_0_0_0_2px_var(--c-focus)]",
                      o.disabled && "cursor-default opacity-45",
                    )}
                  >
                    <Check
                      aria-hidden
                      className={cx("mt-[3px] h-3.5 w-3.5 shrink-0 text-accent-fg", !selected && "invisible")}
                    />
                    <span className="min-w-0">
                      <span className="block break-words text-sm text-fg">{o.label}</span>
                      {o.hint && <span className="block text-xs text-fg-muted">{o.hint}</span>}
                    </span>
                  </li>
                </Fragment>
              );
            })}
          </ul>,
          document.body,
        )}
    </div>
  );
}
